import * as Sentry from '@sentry/nextjs'
import { logger } from 'utils/logger'

declare const gtag: any

const NEXT_PUBLIC_GOOGLE_ANALYTICS = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS

/**
 * Send a conversion action into Google.
 *
 * Because **Segment** doesn't allow us fine-grain control over what conversion
 * eevents to send where from browser-side integrations, this allows us to fire
 * off a specific conversion action.
 *
 * See more info here: https://support.google.com/google-ads/answer/6331304
 *
 * @param conversion The CONVERSION_ID/CONVERSION_LABEL as specified
 * by Google. You can get this value by viewing the details of the Conversion
 * Action in Google Ads.
 * @param value The value of the conversion.
 */
export function sendConversion(conversion: string, value = 1.0): Promise<void> {
  return new Promise((resolve) => {
    logger.debug(
      `DEBUG: gtag('event', 'conversion', { send_to: ${conversion}, value: ${value} })`,
    )
    if (process.env.DEPLOYMENT_TARGET !== 'production') {
      return resolve()
    }

    const timeout = setTimeout(() => {
      const err = new Error('gtag timed out before event_callback')
      Sentry.captureException(err)
      resolve()
    }, 2000)

    try {
      gtag('event', 'conversion', {
        send_to: conversion,
        value,
        currency: 'USD',
        event_callback: () => {
          clearTimeout(timeout)
          resolve()
        },
      })
    } catch (err) {
      Sentry.captureException(err)
      clearTimeout(timeout)
      resolve()
    }
  })
}

export type GaSessionInfo = {
  ga_session_id: string
  ga_session_number: string
}

const EMPTY_GA_SESSION_INFO = {
  ga_session_id: null,
  ga_session_number: null,
} as const

export const getGaSessionInfo = async (): Promise<
  GaSessionInfo | { [K in keyof GaSessionInfo]: null }
> => {
  // The gtag.js script is only available in client contexts, not server
  // contexts. When it is unavailable, the commands that `gtag` pushes to the
  // `dataLayer` global are never processed, so `resolve` is never called, which
  // can cause timeouts in server contexts such as pre-rendering. To prevent
  // this, we instead bail out early in server contexts.
  if (typeof window === 'undefined') {
    return EMPTY_GA_SESSION_INFO
  }

  const gaSessionIdPromise = new Promise((resolve) => {
    gtag('get', NEXT_PUBLIC_GOOGLE_ANALYTICS, 'session_id', resolve)
  })
  const gaSessionNumberPromise = new Promise((resolve) => {
    gtag('get', NEXT_PUBLIC_GOOGLE_ANALYTICS, 'session_number', resolve)
  })

  try {
    const [gaSessionId, gaSessionNumber] = await Promise.all([
      gaSessionIdPromise,
      gaSessionNumberPromise,
    ])

    return {
      ga_session_id: gaSessionId as string,
      ga_session_number: gaSessionNumber as string,
    }
  } catch (originalError) {
    const error = new Error(
      `Error while fetching Google Analytics session info`,
      {
        cause: originalError,
      },
    )
    Sentry.captureException(error)

    return EMPTY_GA_SESSION_INFO
  }
}
